import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Ban, Printer, History } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { money, fmtDateTime } from "../lib/format";
import { usePos } from "../context/PosContext";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import ReceiptModal, { ReceiptBody } from "../components/pos/ReceiptModal";
import PaymentModal from "../components/pos/PaymentModal";

import { printReceipt } from "../lib/printer";
import { getPrinterProfile } from "../lib/printer/profile";
export default function OrdersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { outletId, shift } = usePos();
  const { session, hasPerm } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [showVoid, setShowVoid] = useState(false);
  const [showClosePayment, setShowClosePayment] = useState(false);
  const [closeBillBusy, setCloseBillBusy] = useState(false);
  const [closeBillRequestId, setCloseBillRequestId] = useState(null);
  const [receiptOrder, setReceiptOrder] = useState(null);
  const [historyOrder, setHistoryOrder] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [selfOrderConfirmOrder, setSelfOrderConfirmOrder] = useState(null);
  const [selfOrderConfirmBusy, setSelfOrderConfirmBusy] = useState(false);
  const [selfOrderConfirmRequestId, setSelfOrderConfirmRequestId] = useState(null);
  const [selfOrderCancelReason, setSelfOrderCancelReason] = useState("");
  const [showSelfOrderCancel, setShowSelfOrderCancel] = useState(false);
  const [selfOrderCancelBusy, setSelfOrderCancelBusy] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState("");

  const loadCatalog = useCallback(() => {
    if (!outletId) return;

    setCatalogError("");

    api.get("/pos/catalog", { params: { outlet_id: outletId } })
      .then((r) => setCatalog(r.data))
      .catch((e) => setCatalogError(apiError(e)));
  }, [outletId]);

  const load = useCallback(() => {
    const params = { limit: 50 };
    if (outletId) params.outlet_id = outletId;
    if (q) params.q = q;
    if (status) params.status = status;
    api.get("/orders", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [outletId, q, status]);

    useEffect(() => {
      setCatalog(null);
      loadCatalog();
    }, [loadCatalog]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    const orderId = searchParams.get("order_id");

    if (!orderId) return;

    let cancelled = false;

    const openOrderFromNotification = async () => {
      try {
        const { data: order } = await api.get(`/orders/${orderId}`);

        if (cancelled) return;

        setSelected(order);
      } catch (e) {
        if (cancelled) return;

        toast.error(`Gagal membuka order dari notifikasi: ${apiError(e)}`);
      }
    };

    openOrderFromNotification();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const startCloseBill = () => {
    if (!selected || selected.status !== "OPEN") return;

    if (!shift?.id) {
      toast.error("Shift aktif tidak ditemukan.");
      return;
    }

    setCloseBillRequestId(crypto.randomUUID());
    setShowClosePayment(true);
  };

  const handleCloseBill = async (checkout) => {
    if (!selected?.id) return;

    if (!shift?.id) {
      toast.error("Shift aktif tidak ditemukan.");
      return;
    }

    const requestId = closeBillRequestId || crypto.randomUUID();

    try {
      setCloseBillBusy(true);

      const payload = {
        shift_id: shift.id,
        client_request_id: requestId,
        payment: checkout.payment,
      };

      const { data: updated } = await api.post(
        `/orders/${selected.id}/close-bill`,
        payload
      );

      setShowClosePayment(false);
      setCloseBillRequestId(null);
      setSelected(null);
      setReceiptOrder(updated);

      toast.success(t("Open Bill berhasil ditutup."));
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCloseBillBusy(false);
    }
  };

  const doVoid = async () => {
    try {
      const { data: updated } = await api.post(`/orders/${selected.id}/void`, { reason: voidReason });
      setSelected(updated);
      setShowVoid(false);
      toast.success(t("Order voided"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };


  const startSelfOrderConfirm = (order) => {
    if (!order?.id) return;
    if (order.bill_type !== "SELF_ORDER") return;
    if (order.status !== "PAYMENT_SUBMITTED") return;

    setSelfOrderConfirmRequestId(crypto.randomUUID());
    setSelfOrderConfirmOrder(order);
  };

  const handleSelfOrderConfirm = async (checkout) => {
    if (!selfOrderConfirmOrder?.id) return;

    const requestId =
      selfOrderConfirmRequestId || crypto.randomUUID();

    try {
      setSelfOrderConfirmBusy(true);

      const payload = {
        client_request_id: requestId,
        payment: checkout.payment,
      };

      const { data: updated } = await api.post(
        `/orders/${selfOrderConfirmOrder.id}/self-order/confirm-payment`,
        payload
      );

      setSelfOrderConfirmOrder(null);
      setSelfOrderConfirmRequestId(null);

      toast.success(t("Pembayaran Self Order berhasil dikonfirmasi."));

      await load();

      if (updated?.status === "PAID") {
        setSelected(updated);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSelfOrderConfirmBusy(false);
    }
  };

  const handleSelfOrderCancel = async () => {
    if (!selected?.id) return;
    if (selected.bill_type !== "SELF_ORDER") return;

    if (!["PENDING_PAYMENT", "PAYMENT_SUBMITTED"].includes(selected.status)) {
      return;
    }

    const reason = selfOrderCancelReason.trim();

    if (!reason) {
      toast.error("Alasan pembatalan wajib diisi.");
      return;
    }

    try {
      setSelfOrderCancelBusy(true);

      const { data: updated } = await api.post(
        `/orders/${selected.id}/self-order/cancel`,
        { reason }
      );

      setSelected(updated);
      setShowSelfOrderCancel(false);
      setSelfOrderCancelReason("");

      toast.success(t("Self Order berhasil dibatalkan."));

      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSelfOrderCancelBusy(false);
    }
  };

  const openHistory = async (order) => {
    if (!order?.id) return;

    setHistoryOrder(order);
    setHistoryData(null);
    setHistoryBusy(true);

    try {
      const { data } = await api.get(`/orders/${order.id}/history`);
      setHistoryData(data);
    } catch (e) {
      toast.error(apiError(e));
      setHistoryOrder(null);
    } finally {
      setHistoryBusy(false);
    }
  };

  const closeHistory = () => {
    setHistoryOrder(null);
    setHistoryData(null);
    setHistoryBusy(false);
  };

  const startAddOrder = () => {
      if (!selected || selected.status !== "OPEN") return;

      sessionStorage.setItem(
        "gloos_active_open_bill",
        JSON.stringify({
          id: selected.id,
          transaction_number: selected.transaction_number,
          customer_name: selected.customer_name || "",
        })
      );

      setSelected(null);
      setShowVoid(false);
      navigate("/pos");
    };

  return (
    <div data-testid="orders-page">
      <PageHeader title={t("Orders")} subtitle={t("All transactions for the selected outlet")} testid="orders-header" />
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="orders-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Search transaction number…")}
            className="w-full h-11 pl-9 pr-3 rounded-xl bg-card border border-border focus:border-primary focus:outline-none text-sm"
          />
        </div>
        <select
          data-testid="orders-status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-11 px-3 rounded-xl bg-card border border-border text-sm focus:border-primary focus:outline-none"
        >
          <option value="">{t("All statuses")}</option>
          <option value="OPEN">OPEN</option>
            <option value="PAID">PAID</option>
          <option value="VOID">VOID</option>
        </select>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">{t("Transaction")}</th>
              <th className="px-4 py-3">{t("Customer Name")}</th>
              <th className="px-4 py-3 hidden md:table-cell">{t("Cashier")}</th>
              <th className="px-4 py-3 hidden sm:table-cell">{t("Payment")}</th>
              <th className="px-4 py-3">{t("History")}</th>
                <th className="px-4 py-3">{t("Order Type")}</th>
              <th className="px-4 py-3">{t("Status")}</th>
              <th className="px-4 py-3 text-right">{t("Total")}</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((o) => (
              <tr
                key={o.id}
                data-testid={`order-row-${o.transaction_number}`}
                onClick={() => setSelected(o)}
                className="border-b border-border/50 hover:bg-secondary/40 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3">
                  <p className="font-mono text-xs text-primary">{o.transaction_number}</p>
                  <p className="text-[11px] text-muted-foreground">{fmtDateTime(o.created_at)}</p>
                </td>
                <td className="px-4 py-3 font-medium">
                  {o.customer_name || "—"}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">{o.cashier?.name}</td>
                <td className="px-4 py-3 hidden sm:table-cell">{o.payment?.method_name}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openHistory(o);
                    }}
                    data-testid={`order-history-button-${o.transaction_number}`}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-secondary border border-border text-xs font-semibold hover:bg-accent"
                  >
                    <History size={14} />
                    {t("History")}
                  </button>
                </td>
                <td className="px-4 py-3" data-testid="order-type-cell">
                  <span className="px-2 py-1 rounded-md bg-secondary border border-border text-[11px] font-bold">
                    {o.bill_type === "SELF_ORDER"
                      ? t("Self Order")
                      : o.bill_type === "OPEN_BILL"
                        ? t("Open Bill")
                        : t("Regular")}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-md text-[11px] font-bold ${o.status === "PAID"
                        ? "bg-emerald-500/15 text-emerald-400"
                        : o.status === "OPEN"
                          ? "bg-amber-500/15 text-amber-400"
                          : "bg-red-500/15 text-red-400"}`}>
                    {o.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono font-bold">{money(o.grand_total)}</td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">{t("No orders found.")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {historyOrder && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeHistory}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-card border border-border rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="order-history-modal"
          >
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  {t("Order History")}
                </p>

                <p className="text-sm font-semibold text-foreground mt-1">
                  {historyData?.outlet_name || historyOrder.outlet?.name || "—"}
                </p>

                <p className="font-mono text-sm text-primary mt-1">
                  {historyOrder.transaction_number}
                </p>

                <p className="text-xs text-muted-foreground mt-1">
                  {historyOrder.customer_name || "—"}
                </p>
              </div>

              <button
                onClick={closeHistory}
                className="h-9 px-3 rounded-lg bg-secondary border border-border text-sm font-semibold hover:bg-accent"
              >
                {t("Close")}
              </button>
            </div>

            {historyBusy && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                {t("Loading history…")}
              </div>
            )}

            {!historyBusy && historyData && (
              <div className="p-5 space-y-4">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 rounded-md bg-secondary border border-border">
                    {t("Status:")} <strong>{historyData.status}</strong>
                  </span>

                  {historyData.bill_type && (
                    <span className="px-2 py-1 rounded-md bg-secondary border border-border">
                      {t("Type:")} <strong>{historyData.bill_type}</strong>
                    </span>
                  )}
                </div>

                <div className="space-y-3">
                  {(historyData.events || []).map((event, index) => {
                    const eventLabel =
                      event.type === "OPENED"
                        ? "OPENED"
                        : event.type === "ADD_ORDER"
                          ? `ADD ORDER #${event.sequence || index + 1}`
                          : event.type === "CLOSE_BILL"
                            ? t("CLOSE BILL")
                            : event.type;

                    const details = event.details || {};
                    const items = details.items || [];
                    const totalQty = items.reduce(
                      (sum, item) => sum + (Number(item.qty) || 0),
                      0
                    );

                    const renderOrderDetails = () => (
                      <div className="space-y-3 text-xs">
                        <div className="bg-secondary/50 rounded-xl p-3">
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div>
                              <p className="font-semibold text-sm">
                                {event.type === "OPENED"
                                  ? t("Order #1")
                                  : event.type === "CLOSE_BILL"
                                    ? t("Final Order")
                                    : t("Order Items")}
                              </p>
                              <p className="text-muted-foreground mt-0.5">
                                {details.item_count ?? items.length} {t("item(s)")}
                              </p>
                            </div>

                            <span className="px-2 py-1 rounded-md bg-secondary border border-border text-[11px] font-semibold">
                              {totalQty} {t("qty")}
                            </span>
                          </div>

                          <div className="space-y-2">
                            {items.length > 0 ? (
                              items.map((item, itemIndex) => (
                                <div
                                  key={`${item.product_id || "item"}-${itemIndex}`}
                                  className="rounded-lg border border-border bg-background/40 p-3"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="font-semibold text-sm">
                                        {item.name || t("Unnamed Product")}
                                      </p>

                                      <p className="text-muted-foreground mt-1">
                                        {item.qty ?? 0} × {money(item.unit_price || 0)}
                                      </p>

                                      {item.order_type && (
                                        <p className="text-muted-foreground mt-1">
                                          {t("Order Type:")}{" "}
                                          <strong>{item.order_type}</strong>
                                        </p>
                                      )}

                                      {(item.variants || []).length > 0 && (
                                        <p className="text-muted-foreground mt-1">
                                          {t("Variant:")}{" "}
                                          <strong>{item.variants.join(", ")}</strong>
                                        </p>
                                      )}

                                      {(item.modifiers || []).length > 0 && (
                                        <div className="mt-1">
                                          <p className="text-muted-foreground">
                                            {t("Modifier:")}
                                          </p>
                                          <div className="mt-0.5 space-y-0.5">
                                            {item.modifiers.map(
                                              (modifier, modifierIndex) => (
                                                <p
                                                  key={`${modifier.name || "modifier"}-${modifierIndex}`}
                                                >
                                                  {modifier.name || t("Modifier")}{" "}
                                                  <span className="text-muted-foreground">
                                                    (+{money(modifier.price || 0)})
                                                  </span>
                                                </p>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {item.notes && (
                                        <p className="text-muted-foreground mt-1">
                                          {t("Notes:")} <strong>{item.notes}</strong>
                                        </p>
                                      )}
                                    </div>

                                    <div className="text-right shrink-0">
                                      <p className="text-muted-foreground">
                                        {t("Line Total")}
                                      </p>
                                      <p className="font-bold mt-1">
                                        {money(item.line_total || 0)}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <p className="text-muted-foreground">
                                {t("No item detail available.")}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="bg-secondary/50 rounded-xl p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{t("Subtotal")}</span>
                            <strong>{money(details.subtotal || 0)}</strong>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">
                              {t("Discount")}
                              {details.discount?.label
                                ? ` · ${details.discount.label}`
                                : ""}
                            </span>
                            <strong>
                              {details.discount_amount
                                ? `-${money(details.discount_amount)}`
                                : money(0)}
                            </strong>
                          </div>

                          {(details.tax_amount || 0) > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">
                                {details.tax?.name || t("Tax")}
                                {details.tax?.percent != null
                                  ? ` ${details.tax.percent}%`
                                  : ""}
                                {details.tax?.inclusive ? ` · ${t("Inclusive")}` : ""}
                              </span>
                              <strong>{money(details.tax_amount)}</strong>
                            </div>
                          )}

                          {(details.service_charge_amount || 0) > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">
                                {details.service_charge?.name ||
                                  t("Service Charge")}
                                {details.service_charge?.percent != null
                                  ? ` ${details.service_charge.percent}%`
                                  : ""}
                              </span>
                              <strong>
                                {money(details.service_charge_amount)}
                              </strong>
                            </div>
                          )}

                          <div className="border-t border-border pt-2 flex items-center justify-between">
                            <span className="font-bold">{t("GRAND TOTAL")}</span>
                            <strong className="text-primary">
                              {money(details.grand_total || 0)}
                            </strong>
                          </div>
                        </div>
                      </div>
                    );

                    return (
                      <div
                        key={`${event.type}-${event.sequence || index}`}
                        className="border border-border rounded-xl p-4 space-y-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-bold">{eventLabel}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {fmtDateTime(event.at)}
                            </p>
                          </div>

                          <span className="text-[11px] px-2 py-1 rounded-md bg-secondary border border-border">
                            {event.user?.name ||
                              event.user?.code ||
                              t("Unknown user")}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          <div className="bg-secondary/50 rounded-lg p-3">
                            <p className="text-muted-foreground">{t("User")}</p>
                            <p className="font-semibold mt-1">
                              {event.user?.name || "—"}
                              {event.user?.code
                                ? ` (${event.user.code})`
                                : ""}
                            </p>
                          </div>

                          <div className="bg-secondary/50 rounded-lg p-3">
                            <p className="text-muted-foreground">{t("Shift")}</p>
                            <p className="font-semibold mt-1">
                              {event.shift?.cashier_name || "—"}
                            </p>
                            <p className="text-muted-foreground mt-1">
                              {event.shift?.status || ""}
                            </p>
                          </div>
                        </div>

                        {event.type === "OPENED" && (
                          <div className="space-y-2">
                            <div className="text-xs bg-secondary/50 rounded-lg p-3">
                              <p>
                                {t("Customer:")}{" "}
                                <strong>
                                  {details.customer_name || "—"}
                                </strong>
                              </p>
                            </div>
                            {renderOrderDetails()}
                          </div>
                        )}

                        {event.type === "ADD_ORDER" && renderOrderDetails()}

                        {event.type === "CLOSE_BILL" && (
                          <div className="space-y-3">
                            {renderOrderDetails()}

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                              <div className="bg-secondary/50 rounded-lg p-3">
                                <p className="text-muted-foreground">{t("Payment")}</p>
                                <p className="font-semibold mt-1">
                                  {event.details?.method_name || "—"}
                                </p>
                              </div>

                              <div className="bg-secondary/50 rounded-lg p-3">
                                <p className="text-muted-foreground">{t("Paid")}</p>
                                <p className="font-semibold mt-1">
                                  {money(event.details?.amount_paid || 0)}
                                </p>
                              </div>

                              <div className="bg-secondary/50 rounded-lg p-3">
                                <p className="text-muted-foreground">{t("Change")}</p>
                                <p className="font-semibold mt-1">
                                  {money(event.details?.change || 0)}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => {
            if (selfOrderCancelBusy) return;
            setSelected(null);
            setShowVoid(false);
            setShowSelfOrderCancel(false);
            setSelfOrderCancelReason("");
          }}>
          <div className="w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="order-detail-modal">
            <ReceiptBody order={selected} brand={session?.tenant?.brand_name || "GLOO POS"} />
            <div className="flex flex-wrap gap-2">
                {selected?.bill_type === "SELF_ORDER" &&
          selected?.status === "PAYMENT_SUBMITTED" && (
            <button
              onClick={() => startSelfOrderConfirm(selected)}
              data-testid="self-order-confirm-payment-button"
              className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 hover:opacity-90"
            >
              Confirm Payment
            </button>
          )}

                  {selected?.bill_type === "SELF_ORDER" &&
           ["PENDING_PAYMENT", "PAYMENT_SUBMITTED"].includes(selected?.status) && (
            <button
              onClick={() => {
                setSelfOrderCancelReason("");
                setShowSelfOrderCancel(true);
              }}
              data-testid="self-order-cancel-button"
              className="flex-1 min-w-[140px] h-11 rounded-xl bg-red-500/15 border border-red-500/40 text-red-400 font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-500/25"
            >
              <Ban size={15} /> {t("Cancel Order")}
            </button>
          )}

{selected.status === "OPEN" && (
                  <>
                  <button
                    onClick={startCloseBill}
                    data-testid="order-close-bill-button"
                    className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 hover:opacity-90"
                  >
                    {t("Close Bill")}
                  </button>

                  <button
                    onClick={startAddOrder}
                    data-testid="order-add-button"
                    className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 hover:opacity-90"
                  >
                    {t("Add Order")}
                  </button>
                  </>
                )}

              <button onClick={() =>
                      printReceipt({
                        order: selected,
                        documentType: "receipt",
                        printer: getPrinterProfile(),
                      })
                    } data-testid="order-reprint-button" className="flex-1 h-11 rounded-xl bg-secondary border border-border font-bold text-sm flex items-center justify-center gap-2 hover:bg-accent">
                <Printer size={15} /> {t("Reprint")}
              </button>
              {hasPerm("orders.void") && selected.status === "PAID" && (
                <button onClick={() => setShowVoid(true)} data-testid="order-void-button" className="flex-1 h-11 rounded-xl bg-red-500/15 border border-red-500/40 text-red-400 font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-500/25">
                  <Ban size={15} /> {t("Void")}
                </button>
              )}
            </div>
                          {showSelfOrderCancel && (
                <div className="bg-card border border-red-500/30 rounded-xl p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold">
                      {t("Cancel this Self Order?")}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("This order will be cancelled and will not be processed as a payment.")}
                    </p>
                  </div>

                  <input
                    data-testid="self-order-cancel-reason-input"
                    value={selfOrderCancelReason}
                    onChange={(e) => setSelfOrderCancelReason(e.target.value)}
                    placeholder={t("Cancellation reason")}
                    maxLength={500}
                    disabled={selfOrderCancelBusy}
                    className="w-full h-10 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
                  />

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (selfOrderCancelBusy) return;
                        setShowSelfOrderCancel(false);
                        setSelfOrderCancelReason("");
                      }}
                      disabled={selfOrderCancelBusy}
                      className="h-10 rounded-lg bg-secondary border border-border font-bold text-sm disabled:opacity-50"
                    >
                      {t("Back")}
                    </button>

                    <button
                      type="button"
                      onClick={handleSelfOrderCancel}
                      disabled={selfOrderCancelBusy || !selfOrderCancelReason.trim()}
                      data-testid="self-order-cancel-confirm-button"
                      className="h-10 rounded-lg bg-red-500 text-white font-bold text-sm disabled:opacity-50"
                    >
                      {selfOrderCancelBusy ? t("Cancelling...") : t("Cancel Order")}
                    </button>
                  </div>
                </div>
              )}

{showVoid && (
              <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold">{t("Void this order? The record stays auditable.")}</p>
                <input
                  data-testid="void-reason-input"
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder={t("Reason")}
                  className="w-full h-10 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
                />
                <button onClick={doVoid} data-testid="void-confirm-button" className="w-full h-10 rounded-lg bg-red-500 text-white font-bold text-sm">{t("Confirm Void")}</button>
              </div>
            )}
          </div>
        </div>
      )}
        {selfOrderConfirmOrder && (
          <PaymentModal
            total={selfOrderConfirmOrder.grand_total}
            paymentMethods={catalog?.payment_methods || []}
            onClose={() => {
              if (!selfOrderConfirmBusy) {
                setSelfOrderConfirmOrder(null);
                setSelfOrderConfirmRequestId(null);
              }
            }}
            onPay={handleSelfOrderConfirm}
            busy={selfOrderConfirmBusy}
            mode="CLOSE_BILL"
          />
        )}

        {showClosePayment && selected && (
          <PaymentModal
            total={selected.grand_total}
            paymentMethods={catalog?.payment_methods || []}
            onClose={() => {
              setShowClosePayment(false);
              setCloseBillRequestId(null);
            }}
            onPay={handleCloseBill}
            busy={closeBillBusy}
            mode="CLOSE_BILL"
          />
        )}

        {receiptOrder && (
          <ReceiptModal
            order={receiptOrder}
            onClose={() => setReceiptOrder(null)}
          />
        )}

    </div>
  );
}
